import { computed, onBeforeUnmount, toValue, watch } from "vue";
import type { MaybeRefOrGetter, Ref } from "vue";

type PopupSize = {
  width: number;
  height: number;
};

export type PopupPosition = {
  x: number;
  y: number;
};

type PopupBehaviorOptions = {
  visible: MaybeRefOrGetter<boolean>;
  position: MaybeRefOrGetter<PopupPosition>;
  popupRef: Ref<HTMLElement | null>;
  size?: MaybeRefOrGetter<PopupSize>;
  margin?: number;
  onClose: () => void;
};

type PopupViewport = {
  width: number;
  height: number;
};

const DEFAULT_SIZE: PopupSize = { width: 520, height: 360 };
const DEFAULT_MARGIN = 8;

const isFiniteNumber = (value: number) => Number.isFinite(value);

const clamp = (value: number, min: number, max: number) => {
  if (min > max) return min;
  return Math.min(Math.max(value, min), max);
};

const normalizeSize = (size: PopupSize, fallback: PopupSize): PopupSize => {
  if (isFiniteNumber(size.width) && size.width > 0 && isFiniteNumber(size.height) && size.height > 0) {
    return size;
  }
  return fallback;
};

export const computePopupPosition = (params: {
  position: PopupPosition;
  size: PopupSize;
  viewport?: PopupViewport;
  margin?: number;
}) => {
  const margin = params.margin ?? DEFAULT_MARGIN;
  const x = isFiniteNumber(params.position.x) ? params.position.x : 0;
  const y = isFiniteNumber(params.position.y) ? params.position.y : 0;

  if (!params.viewport) {
    return { left: x, top: y };
  }

  const maxLeft = params.viewport.width - params.size.width - margin;
  const maxTop = params.viewport.height - params.size.height - margin;

  return {
    left: clamp(x, margin, maxLeft),
    top: clamp(y, margin, maxTop),
  };
};

export function usePopupBehavior(options: PopupBehaviorOptions) {
  const hasWindow = typeof window !== "undefined";
  const margin = options.margin ?? DEFAULT_MARGIN;

  const getPopupSize = (): PopupSize => {
    if (options.size) {
      return normalizeSize(toValue(options.size), DEFAULT_SIZE);
    }
    const rect = options.popupRef.value?.getBoundingClientRect();
    if (rect?.width && rect?.height) {
      return { width: rect.width, height: rect.height };
    }
    return DEFAULT_SIZE;
  };

  const popupStyle = computed(() => {
    const { x, y } = toValue(options.position);
    const size = getPopupSize();
    const viewport = hasWindow ? { width: window.innerWidth, height: window.innerHeight } : undefined;
    const { left, top } = computePopupPosition({
      position: { x, y },
      size,
      viewport,
      margin,
    });

    return {
      left: `${left}px`,
      top: `${top}px`,
    };
  });

  const handleGlobalPointer = (event: PointerEvent) => {
    if (!options.popupRef.value) return;
    if (!options.popupRef.value.contains(event.target as Node)) {
      options.onClose();
    }
  };

  const handleGlobalKey = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      options.onClose();
    }
  };

  const detachListeners = () => {
    if (!hasWindow) return;
    window.removeEventListener("pointerdown", handleGlobalPointer);
    window.removeEventListener("keydown", handleGlobalKey);
  };

  watch(
    () => toValue(options.visible) && hasWindow,
    (active) => {
      detachListeners();
      if (!active) return;
      window.addEventListener("pointerdown", handleGlobalPointer);
      window.addEventListener("keydown", handleGlobalKey);
      requestAnimationFrame(() => options.popupRef.value?.focus());
    },
    { immediate: true },
  );

  onBeforeUnmount(detachListeners);

  return {
    popupStyle,
  };
}
